import { useCallback } from 'react';
import posthog from 'posthog-js';

export const usePostHog = () => {
  const trackEvent = useCallback((eventName, properties = {}) => {
    try {
      // Add common properties to all events
      const enhancedProperties = {
        ...properties,
        timestamp: new Date().toISOString(),
        app_version: process.env.REACT_APP_VERSION || '1.0.0',
        user_agent: navigator.userAgent,
        screen_size: `${window.screen.width}x${window.screen.height}`,
        viewport_size: `${window.innerWidth}x${window.innerHeight}`,
        url: window.location.href,
        referrer: document.referrer,
      };

      posthog.capture(eventName, enhancedProperties);
      console.log(`PostHog Event: ${eventName}`, enhancedProperties);
    } catch (error) {
      console.error('PostHog tracking error:', error);
    }
  }, []);

  const identifyUser = useCallback((userId, properties = {}) => {
    try {
      // Get additional user context from localStorage if available
      const userInfoStr = localStorage.getItem("userInfo");
      let enhancedProperties = { ...properties };
      
      if (userInfoStr) {
        try {
          const userInfo = JSON.parse(userInfoStr);
          enhancedProperties = {
            ...enhancedProperties,
            phone_number: userInfo?.data?.phone || "",
            email: userInfo?.data?.email || "",
            account_status: userInfo?.data?.status || "",
            level: userInfo?.data?.level || 0,
            xp: userInfo?.data?.xp || 0,
            coins: userInfo?.data?.token || 0,
          };
        } catch (e) {
          console.warn('Could not parse user info for PostHog identification');
        }
      }

      // Force identification with database consumer ID
      console.log(`🔄 Forcing PostHog identification with userId: ${userId}`);
      
      // Identify with our database consumer ID (don't reset - let PostHog merge sessions)
      posthog.identify(userId.toString(), enhancedProperties);
      
      // Also update person properties to ensure profile consistency
      posthog.setPersonProperties(enhancedProperties);
      
      // Check if identification took effect after a brief delay
      setTimeout(() => {
        const currentId = posthog.get_distinct_id();
        console.log(`✅ PostHog User Identified (confirmed): ${userId}`, enhancedProperties);
        console.log(`🔍 Current distinct_id: ${currentId}`);
        
        // Only retry if identification failed
        if (currentId !== userId.toString()) {
          console.log('⚠️ Identification mismatch, retrying...');
          posthog.identify(userId.toString(), enhancedProperties);
        }
      }, 1000);
      
      console.log(`PostHog User Identified: ${userId}`, enhancedProperties);
    } catch (error) {
      console.error('PostHog identify error:', error);
    }
  }, []);

  const setUserProperties = useCallback((properties) => {
    try {
      posthog.setPersonProperties(properties);
      console.log('PostHog User Properties Set:', properties);
    } catch (error) {
      console.error('PostHog user properties error:', error);
    }
  }, []);

  const updateUserProfile = useCallback(() => {
    try {
      const userInfoStr = localStorage.getItem("userInfo");
      if (!userInfoStr) return;

      const userInfo = JSON.parse(userInfoStr);
      const userId = userInfo?.data?.id;
      
      if (userId) {
        const userProfile = {
          phone_number: userInfo?.data?.phone || "",
          email: userInfo?.data?.email || "",
          name: userInfo?.data?.name || userInfo?.data?.first_name || "",
          account_status: userInfo?.data?.status || "",
          level: userInfo?.data?.level || 0,
          xp: userInfo?.data?.xp || 0,
          coins: userInfo?.data?.token || 0,
          last_updated: new Date().toISOString()
        };

        posthog.setPersonProperties(userProfile);
        console.log('PostHog User Profile Updated:', userProfile);
      }
    } catch (error) {
      console.error('PostHog update user profile error:', error);
    }
  }, []);

  const trackTimer = useCallback((eventName) => {
    const startTime = Date.now();
    
    return {
      end: (properties = {}) => {
        const duration = Date.now() - startTime;
        trackEvent(eventName, {
          ...properties,
          duration_ms: duration,
          duration_seconds: Math.round(duration / 1000)
        });
      }
    };
  }, [trackEvent]);

  return { trackEvent, identifyUser, setUserProperties, updateUserProfile, trackTimer };
};